/* eslint-disable  */
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteOutline } from '@mui/icons-material';
import { Button, Card, Flex, Form, Input, notification, Spin, Switch } from 'antd';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { OverViewCardForm } from '../OverviewCardForm';
import {
	QueryObserverResult,
	RefetchOptions,
	useMutation,
	UseMutationResult,
} from '@tanstack/react-query';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { closeModal } from '@/store/modal/slice';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import './TextingWorkflowInfo.scss';
import { User } from '@/types/TAccounts';
import ImageUpload from './ImageUpload';
import { uploadToS3 } from '@/api/voice';
import ShortUniqueId from 'short-unique-id';
import { FormActionType, WorkflowType } from 'src/enums/EWorkflow';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';
import { useFeatureGating } from 'src/Hooks/useFeatureGating';
import { EFeatureKeys } from 'src/enums/EFeatureKeys';
import { EFeatureCategories } from 'src/enums/EFeatureCategories';

const uid = new ShortUniqueId({ length: 10 });

interface TextingWorkflowFormProps {
	initialValues?: any;
	showAddFieldButton?: boolean;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	actionType: FormActionType;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	scenarioIndex?: number;
	debrand: boolean;
	isNoviUser: boolean;
	disableSubmittion?: boolean;
}

export interface TextingWorkflowFormRef {
	form: any;
}

const TextingWorkflowForm = forwardRef<TextingWorkflowFormRef, TextingWorkflowFormProps>(
	(
		{
			initialValues,
			showAddFieldButton = true,
			refetch,
			actionType,
			mutation,
			scenarioIndex,
			debrand,
			isNoviUser,
			disableSubmittion,
		},
		ref,
	) => {
		const [form] = Form.useForm();
		const dispatch = useAppDispatch();
		const { isFeatureUnlimited, getFeatureNumericValue } = useFeatureGating();
		const selectedAgentId = useAppSelector(getSelectedAgentId);
		const selectedAgent = useAppSelector(getSelectedAgent);
		const canAddScenario = useAppSelector(selectCanAddScenario);
		const isTextWorkflowsUnlimited = isFeatureUnlimited(
			EFeatureKeys.TEXT_WORKFLOWS,
			EFeatureCategories.WORKFLOWS,
		);
		const textWorkflowsLimit = getFeatureNumericValue(
			EFeatureKeys.TEXT_WORKFLOWS,
			EFeatureCategories.WORKFLOWS,
		);

		const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);

		const [imageFiles, setImageFiles] = useState<{ [key: string]: { file: File; s3Key?: string } }>(
			{},
		);
		const [existingImages, setExistingImages] = useState<{
			[key: string]: { s3Key: string; presignedUrl: string };
		}>({});

		const { mutate: uploadToS3Fn } = useMutation({
			mutationKey: ['uploadToS3'],
			mutationFn: uploadToS3,
			onError: (error: any) => {
				notification.error({
					message: error.message || 'An error occurred during the upload.',
				});
			},
		});

		useImperativeHandle(
			ref,
			() => ({
				form,
			}),
			[form],
		);

		// Updated useEffect with better state management
		useEffect(() => {
			console.log('🔄 Main useEffect triggered:', {
				actionType,
				scenarioIndex,
				hasInitialValues: !!initialValues,
			});

			if (initialValues && actionType === FormActionType.EDIT) {
				const formValues = {
					workflows: initialValues.workflows.map((workflow: any) => ({
						...workflow,
						id: workflow.id,
						availability: workflow.availability || null,
						configEnabled: workflow.configEnabled ?? false,
						fallbackMessage: workflow.fallbackMessage || '',
					})),
				};
				form.setFieldsValue(formValues);

				// Clear image files and set existing images for EDIT mode
				setImageFiles({});

				// Track existing image for the scenario being edited
				const editingScenario = initialValues.workflows[0];
				if (editingScenario?.imageS3Key && editingScenario?.presignedUrl) {
					const scenarioKey = editingScenario.id || `scenario-${scenarioIndex}`;
					setExistingImages({
						[scenarioKey]: {
							s3Key: editingScenario.imageS3Key,
							presignedUrl: editingScenario.presignedUrl,
						},
					});
					console.log(`📝 EDIT mode - tracking existing image for ${scenarioKey}`);
				} else {
					setExistingImages({});
				}
			}

			if (actionType === FormActionType.ADD) {
				form.resetFields();
				setImageFiles({});

				// Track existing images from previousWorkflowData for ADD mode
				const existingImagesMap: { [key: string]: { s3Key: string; presignedUrl: string } } = {};
				if (initialValues?.workflows?.[0]?.previousWorkflowData) {
					initialValues.workflows[0].previousWorkflowData.forEach(
						(workflow: any, index: number) => {
							if (workflow.imageS3Key && workflow.presignedUrl) {
								const scenarioKey = workflow.id || `scenario-${index}`;
								existingImagesMap[scenarioKey] = {
									s3Key: workflow.imageS3Key,
									presignedUrl: workflow.presignedUrl,
								};
							}
						},
					);
				}
				setExistingImages(existingImagesMap);
				console.log('➕ ADD mode - tracked existing images:', Object.keys(existingImagesMap));
			}

			return () => {
				if (actionType === FormActionType.ADD) {
					form.resetFields();
					setImageFiles({});
					setExistingImages({});
				}
			};
		}, [initialValues, form, actionType, scenarioIndex]);

		const handleSubmit = async (values: {
			workflows: {
				id?: string;
				description: string;
				voiceResponse: string;
				textMessage: URL | string;
				askForPermissionOnText: boolean;
				shortURLEnabled: boolean;
				customTextFooter: string;
				imageS3Key?: string;
				presignedUrl?: string;
				postExecutionPrompt?: string;
				availability?: TWorkflowAvailabilities;
				configEnabled?: boolean;
				fallbackMessage?: string;
			}[];
		}) => {
			try {
				if (!isTextWorkflowsUnlimited) {
					if (initialValues?.length || textWorkflowsLimit <= 0) {
						dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
					}
				}

				let formData: {
					id: string;
					description: string;
					textMessage: string | URL;
					voiceResponse: string;
					shortURLEnabled: boolean;
					customTextFooter: string;
					imageS3Key?: string;
					presignedUrl?: string;
					postExecutionPrompt?: string;
					availability?: TWorkflowAvailabilities;
					configEnabled?: boolean;
					fallbackMessage?: string;
				}[] = [];

				let updatedScenario = values.workflows.map((scenario: any, formIndex: number) => {
					const { scenarioKey } = getScenarioImageInfo(formIndex);

					return {
						id: scenario.id ?? uid.rnd(),
						description: scenario.description,
						textMessage: scenario.textMessage,
						voiceResponse: scenario.voiceResponse,
						postExecutionPrompt: scenario.postExecutionPrompt,
						askForPermissionOnText: scenario.askForPermissionOnText,
						shortURLEnabled:
							scenario.shortURLEnabled === undefined ? true : scenario.shortURLEnabled,
						customTextFooter: scenario.customTextFooter,
						imageS3Key: scenario?.imageS3Key,
						presignedUrl: scenario?.presignedUrl,
						availability: scenario.availability,
						configEnabled: scenario.configEnabled ?? false,
						fallbackMessage: scenario.fallbackMessage || '',
					};
				});

				// Handle image uploads and preservation
				updatedScenario = await Promise.all(
					updatedScenario.map(async (scenario, formIndex) => {
						const { scenarioKey } = getScenarioImageInfo(formIndex);
						const newImageFile = imageFiles[scenarioKey]?.file;
						const existingImage = existingImages[scenarioKey];

						let finalImageS3Key = scenario.imageS3Key;

						if (newImageFile) {
							// User uploaded a new image
							console.log(`📤 Uploading new image for ${scenarioKey}`);
							finalImageS3Key = await uploadImageToS3(newImageFile, scenarioKey);
						} else if (existingImage) {
							// Keep existing image
							console.log(
								`📦 Preserving existing image for ${scenarioKey}: ${existingImage.s3Key}`,
							);
							finalImageS3Key = existingImage.s3Key;
						} else {
							// No image
							finalImageS3Key = undefined;
						}

						return {
							...scenario,
							imageS3Key: finalImageS3Key,
						};
					}),
				);

				// checking if it existed before and now is being removed
				const scenario = values.workflows[0];
				const actualScenarioIndex = actionType === FormActionType.EDIT ? (scenarioIndex ?? 0) : 0;
				if (Object.keys(imageFiles || {})?.length <= 0 && scenario?.imageS3Key) {
					updatedScenario[0].imageS3Key = undefined;
				}

				if (initialValues?.workflows[0].previousWorkflowData) {
					//@ts-expect-error ignore for now
					scenario.previousWorkflowData = initialValues.workflows[0].previousWorkflowData;
				}
				//@ts-expect-error ignore for now
				const editingScenarioIndex = scenario?.index ?? 0;
				//@ts-expect-error ignore for now
				if (scenario?.previousWorkflowData) {
					//@ts-expect-error ignore for now
					formData = [...scenario.previousWorkflowData];
				}

				if (actionType === FormActionType.EDIT) {
					const prevFormId = formData[editingScenarioIndex]?.id;

					if (prevFormId && prevFormId !== updatedScenario[0].id) {
						updatedScenario[0].id = prevFormId;
					}

					formData.splice(editingScenarioIndex, updatedScenario.length, ...updatedScenario);
				} else if (actionType === FormActionType.ADD) {
					formData = [...formData, ...updatedScenario];
				}

				await mutation.mutateAsync({ workflows: formData, agentId: selectedAgentId as string });
				refetch();
				notification.success({
					key: 'form-success',
					message: 'Details has been saved successfully!',
					duration: 3,
				});
				form.resetFields();
				setIsFieldTouchReset(false);
				dispatch(closeModal());
				await analyzeAndUpdateWorkflow(
					{ workflows: formData },
					WorkflowType.TEXT,
					dispatch,
					selectedAgentId as string,
				);
			} catch (err) {
				console.log('Error: ', err);
				notification.error({
					message: 'Something went wrong, please try again later!',
				});
			}
		};

		const getScenarioKey = (formIndex: number, scenario?: any) => {
			if (actionType === FormActionType.EDIT) {
				return scenario?.id || `scenario-${scenarioIndex}`;
			} else {
				// For ADD mode, use the scenario ID from previousWorkflowData or create a new key
				const existingScenario = initialValues?.workflows?.[0]?.previousWorkflowData?.[formIndex];
				return existingScenario?.id || `new-${formIndex}`;
			}
		};

		// 4. Helper function to get scenario data and image info
		const getScenarioImageInfo = (formIndex: number) => {
			let scenarioData;
			let scenarioKey;

			if (actionType === FormActionType.EDIT && formIndex === 0) {
				// EDIT mode: only the first form field represents the scenario being edited
				scenarioData = initialValues?.workflows?.[0];
				scenarioKey = getScenarioKey(formIndex, scenarioData);
			} else if (actionType === FormActionType.ADD) {
				// ADD mode: form fields represent existing scenarios + new ones
				scenarioData = initialValues?.workflows?.[0]?.previousWorkflowData?.[formIndex];
				scenarioKey = getScenarioKey(formIndex, scenarioData);
			} else {
				// EDIT mode but not the first field (shouldn't happen in current design)
				return {
					scenarioData: null,
					scenarioKey: `new-${formIndex}`,
					hasImage: false,
					presignedUrl: '',
				};
			}

			const hasExistingImage = !!scenarioData?.imageS3Key;
			const presignedUrl = hasExistingImage ? scenarioData?.presignedUrl || '' : '';

			console.log(`🔍 Scenario info for form field ${formIndex}:`, {
				actionType,
				scenarioKey,
				hasExistingImage,
				imageS3Key: scenarioData?.imageS3Key,
				hasPresignedUrl: !!presignedUrl,
			});

			return {
				scenarioData,
				scenarioKey,
				hasImage: hasExistingImage,
				presignedUrl: hasExistingImage ? presignedUrl : '',
			};
		};

		// 5. Updated uploadImageToS3 function
		const uploadImageToS3 = async (file: File, scenarioKey: string) => {
			const formData = new FormData();
			const now = new Date();
			const timestamp = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${now.getFullYear()}${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
			const fileExtension = file?.name.substring(file?.name.lastIndexOf('.'));
			const s3Key = `${selectedAgent?.number}_${timestamp}${fileExtension}`;

			formData.append('file', file);
			formData.append('s3Key', s3Key);

			try {
				await uploadToS3Fn(formData);
				setImageFiles((prev) => ({
					...prev,
					[scenarioKey]: { file, s3Key },
				}));
				return s3Key;
			} catch {
				notification.error({
					message: 'Image upload failed',
					description: 'Could not upload image to S3',
				});
				return null;
			}
		};

		const baseClass = 'texting-workflow';
		const handleConfirmRemove = (remove: (name: number) => void, name: number) => {
			ConfirmDeleteModal({
				itemName: `Scenario`,
				onConfirm: () => remove(name),
			});
		};

		const handleAddTextingWorkflow = (add: () => void) => {
			if (!canAddScenario) {
				notification.error({
					message: 'Scenario limit reached',
					description: 'Max scenarios (100) reached. Please Delete existing to add more.',
				});
				return;
			}

			if (isTextWorkflowsUnlimited) {
				add();
				return;
			}

			if (initialValues?.length || textWorkflowsLimit > 0) {
				add();
			} else {
				dispatch(openModal({ modalId: MODAL_IDS.SUBSCRIPTION_MODAL }));
			}
		};

		const FormItems = () => (
			<Form.List name="workflows" initialValue={[{}]}>
				{(fields, { add, remove }) => (
					<>
						{fields.map(({ key, name, ...restField }, formIndex) => {
							const { scenarioData, scenarioKey, hasImage, presignedUrl } =
								getScenarioImageInfo(formIndex);

							// For EDIT mode, only show the scenario being edited (first field)
							if (actionType === FormActionType.EDIT && formIndex > 0) {
								return null;
							}

							const displayScenarioNumber =
								actionType === FormActionType.EDIT ? (scenarioIndex ?? 0) + 1 : formIndex + 1;

							return (
								<Flex key={key} className="mb-2" gap={8}>
									<Card
										className="flex-1"
										title={
											<div className="flex justify-between">
												<span className="font-bold text-base">
													{`Scenario ${displayScenarioNumber}`}
												</span>
												{fields.length > 1 && showAddFieldButton && (
													<Button
														type="primary"
														danger
														icon={<DeleteOutline />}
														onClick={() => handleConfirmRemove(remove, name)}
													>
														Delete
													</Button>
												)}
											</div>
										}
									>
										<Form.Item {...restField} name={[name, 'id']} hidden>
											<Input />
										</Form.Item>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Enable Short URLs"
												description="Enable short URLs to keep track of the links and get analytics."
											/>
											<Form.Item
												className=" mt-2 pt-2 "
												{...restField}
												name={[name, 'shortURLEnabled']}
											>
												<Switch defaultChecked className=" mt-2 mb-4" />
											</Form.Item>
										</div>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Scenario Description"
												description="Describe the scenario in which you would like to send the caller a text message. The AI is intelligent enough to know when the scenario is triggered."
											/>
											<Form.Item
												className=" mt-2 pt-2 "
												{...restField}
												name={[name, 'description']}
												rules={[
													{
														validator: (_, value) => {
															if (!value || value.trim() === '') {
																return Promise.reject("Description can't be blank");
															}
															return Promise.resolve();
														},
													},
												]}
											>
												<Input.TextArea
													placeholder="e.g. Send the caller a copy of the menu. Execute whenever caller asks for menu or prices."
													autoSize={{ minRows: 1, maxRows: 3 }}
													data-testid="textingworkflow-scenario-desc-input"
												/>
											</Form.Item>
										</div>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2 mb-2"
												label="Text Message"
												description="Specify the text you want your AI receptionist to send when the scenario is triggered."
											/>
											<Form.Item
												className="mt-2 pt-2"
												{...restField}
												name={[name, 'textMessage']}
												rules={[
													{
														validator: (_, value) => {
															if (!value || value.trim() === '') {
																return Promise.reject("Text Message Can't be blank");
															}
															return Promise.resolve();
														},
													},
													{
														max: 1000,
														message: 'Max 1000 characters allowed',
													},
												]}
											>
												<Input.TextArea
													className={`${baseClass}_input_text_area`}
													aria-multiline
													placeholder="e.g. Here is our menu: www.restaurant.com/menu"
													data-testid="textingworkflow-text-message-input"
												/>
											</Form.Item>
											<div className="text-blue-700 text-xs font-semibold mb-2">
												* Max 1000 characters allowed{' '}
											</div>
										</div>

										<div className="space-y-2">
											<div className="border-b bg-white rounded-md">
												<LabelWithDescription
													customClassName="mt-2 mb-2"
													label="What should the AI do next?"
													description="Specify instructions for the AI to follow after it executes this workflow."
												/>
												<Form.Item
													className="mt-2 pt-2"
													{...restField}
													name={[name, 'postExecutionPrompt']}
												>
													<Input.TextArea
														placeholder="e.g., Tell the caller you've sent them a text message, and then trigger the intake form defined earlier to collect their information."
														autoSize={{ maxRows: 2 }}
														data-testid="textingworkflow-text-post-execution-input"
													/>
												</Form.Item>
											</div>
										</div>

										{debrand || isNoviUser ? (
											<div>
												<LabelWithDescription
													customClassName="mt-2"
													label="Custom Text Footer"
													description="Add a custom text footer to your text message."
												/>
												<Form.Item
													className=" mt-2 pt-2 "
													{...restField}
													name={[name, 'customTextFooter']}
													rules={[
														{
															max: 100,
															message: 'Max 100 characters allowed',
														},
													]}
												>
													<Input.TextArea
														placeholder="e.g. Sent by AI Receptionist"
														autoSize={{ minRows: 1, maxRows: 2 }}
													/>
												</Form.Item>
												<div className="text-blue-700 text-xs font-semibold mb-2">
													* Max 100 characters allowed{' '}
												</div>
											</div>
										) : null}

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Ask before sending Text SMS"
												description="If checked in, above message will be sent when the caller confirms for the text sms"
											/>
											<Form.Item
												className="pt-2 "
												{...restField}
												name={[name, 'askForPermissionOnText']}
											>
												<Switch />
											</Form.Item>
										</div>

										<Form.Item label="Image Upload" name={[name, 'image']}>
											<ImageUpload
												key={`image-${scenarioKey}`}
												data-testid="textingworkflow-image-upload-input"
												value={imageFiles[scenarioKey]?.file}
												onChange={(imageFile: File | null) => {
													console.log(
														`🔄 ImageUpload onChange for ${scenarioKey}:`,
														imageFile?.name || 'removed',
													);

													if (imageFile) {
														setImageFiles((prev) => ({
															...prev,
															[scenarioKey]: { file: imageFile },
														}));
														// Remove from existing images if user uploads new file
														setExistingImages((prev) => {
															const newExisting = { ...prev };
															delete newExisting[scenarioKey];
															return newExisting;
														});
													} else {
														// User removed the image
														setImageFiles((prev) => {
															const newFiles = { ...prev };
															delete newFiles[scenarioKey];
															return newFiles;
														});
														// Also remove from existing images if user explicitly removes
														setExistingImages((prev) => {
															const newExisting = { ...prev };
															delete newExisting[scenarioKey];
															return newExisting;
														});
													}
												}}
												// Show presigned URL only if:
												// 1. This specific scenario has an image
												// 2. No new file is being uploaded
												// 3. The existing image hasn't been explicitly removed
												presignedUrl={
													!imageFiles[scenarioKey] && hasImage && existingImages[scenarioKey]
														? presignedUrl
														: ''
												}
											/>
										</Form.Item>
									</Card>
								</Flex>
							);
						})}

						{showAddFieldButton && (
							<Form.Item>
								<Button
									type="dashed"
									onClick={() => handleAddTextingWorkflow(add)}
									block
									icon={<PlusOutlined />}
								>
									Add Texting Workflow
								</Button>
							</Form.Item>
						)}
					</>
				)}
			</Form.List>
		);

		return (
			<>
				<Spin
					spinning={mutation.isPending}
					indicator={
						<LoadingOutlined
							style={{ fontSize: '48px' }}
							spin
							data-testid="textingworkflow-form-spinner"
						/>
					}
				>
					<OverViewCardForm
						form={form}
						initialValues={actionType === FormActionType.EDIT && initialValues}
						formItems={<FormItems />}
						handleSubmit={handleSubmit}
						isFieldTouchReset={isFieldTouchReset}
						disableSubmittion={disableSubmittion}
					/>
				</Spin>
			</>
		);
	},
);

export default TextingWorkflowForm;
